/**
 * FinancialSnapshot.js – summary card showing total assets, liabilities, net worth and expandable annual cash flow.
 * Used in Client Profile (steps 4, 5, 6). Props: factFind.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Wallet, DollarSign, ChevronDown, Info } from 'lucide-react';
import { Card, GradientText, CalculationTooltip, InfoIcon, type TooltipBreakdownRow } from './ui/index.tsx';
import { buildApplicantVoice, formatCurrency } from '../utils/index.ts';
import * as calculationApi from '../services/calculationApi.ts';
import { computeStructureSnapshotBreakdown } from '../utils/structureSnapshotUtils.ts';
import { assetComposition, withEntityShares } from '../utils/assetComposition.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

const buildStructuresFootnote = (possessive: string) =>
    `Trust and company lines show ${possessive} share of that arrangement (the percentages above), net of any debts recorded against it. Age Pension, tax, and care fee estimates use the same split.`;

// calculateFinancials's response is the calc engine's `financials` object — not modelled in
// shared/factFindTypes.ts (see calculationApi.ts's own doc comment) — so this is the subset of
// fields this card actually reads, typed locally rather than as `unknown`/`any`.
interface Financials {
    cashFlow: number;
    cashFlowBreakdown: TooltipBreakdownRow[];
    taxBreakdown: TooltipBreakdownRow[];
    socialSecurityBreakdown: TooltipBreakdownRow[];
    netWorth: number;
}

const EMPTY_FINANCIALS: Financials = { cashFlow: 0, cashFlowBreakdown: [], taxBreakdown: [], socialSecurityBreakdown: [], netWorth: 0 };

interface FinancialSnapshotProps {
    factFind: DraftFactFind;
    /** Reports the net worth this card is DISPLAYING, so a compact presentation of the same card
     *  (the mobile bar) can show the identical figure rather than computing its own and risking
     *  two numbers that disagree on screen. */
    onNetWorthChange?: (netWorth: number) => void;
}

const FinancialSnapshot = ({ factFind, onNetWorthChange }: FinancialSnapshotProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // The composition legend opens by default, unlike the cash flow row below it. Collapsed, that
    // row still shows a meaningful number; a coloured bar with no labels shows nothing at all, so
    // it has to introduce itself once before it can be folded away.
    const [showComposition, setShowComposition] = useState(true);
    const [financials, setFinancials] = useState<Financials>(EMPTY_FINANCIALS);

    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((result) => {
            if (!cancelled) setFinancials(result as Financials);
        });
        return () => { cancelled = true; };
    }, [factFind]);

    const { cashFlow, cashFlowBreakdown, taxBreakdown, socialSecurityBreakdown, netWorth } = financials;

    const isPartnered = factFind.adviceFor === 'couple';
    // "Your share" vs "Bob & Sally's share" — see utils/applicantVoice.ts.
    const voice = useMemo(() => buildApplicantVoice(factFind), [factFind]);
    const structuresFootnote = buildStructuresFootnote(voice.possessive);
    const structureBreakdown = useMemo(
        () => computeStructureSnapshotBreakdown(factFind, isPartnered),
        [factFind, isPartnered]
    );

    const showStructureSplit =
        (factFind.structures || []).length > 0 || factFind.structuresGateAnswer === true;

    const displayNetWorth = showStructureSplit ? structureBreakdown.combinedNetWorth : netWorth;

    // With a trust or company on the file, each entity's share (the figure printed above it) is a
    // slice of its own beside what is owned individually, so the bar still adds up to the card's
    // total (owner, 16 Oct 2026: the bar used to vanish the moment a file had a trust).
    const composition = useMemo(
        () => (showStructureSplit ? withEntityShares(assetComposition(factFind), structureBreakdown.entityLines) : assetComposition(factFind)),
        [factFind, showStructureSplit, structureBreakdown],
    );

    useEffect(() => {
        onNetWorthChange?.(displayNetWorth);
    }, [displayNetWorth, onNetWorthChange]);

    const { totalAssets, totalLiabilities } = structureBreakdown;

    return (
        <Card className="bg-white/80 sticky top-8">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Wallet className="text-blue-500" /> <GradientText>Financial Snapshot</GradientText>
            </h3>
            <div className="space-y-4 text-sm">
                {showStructureSplit ? (
                    <>
                        <div className="flex justify-between items-center">
                            <p className="font-medium text-gray-600">Owned individually</p>
                            <p className="font-semibold text-gray-800">{formatCurrency(structureBreakdown.individually)}</p>
                        </div>
                        {structureBreakdown.entityLines.map((line) => (
                            <div key={line.id} className="flex justify-between items-start gap-2">
                                <p className="font-medium text-gray-600 truncate pr-2">
                                    {line.name}
                                    <span className="block text-xs font-normal text-gray-500">
                                        {voice.Possessive} share ({line.combinedApportionmentPct}%
                                        {line.liabilitiesTotal > 0 ? ', net of debt' : ''})
                                    </span>
                                </p>
                                <p className="font-semibold text-gray-800 shrink-0">{formatCurrency(line.applicantsShare)}</p>
                            </div>
                        ))}
                        <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                            <p className="font-medium text-gray-600">Total assets</p>
                            <p className="font-semibold text-lg text-gray-800">{formatCurrency(totalAssets)}</p>
                        </div>
                    </>
                ) : (
                    <div className="flex justify-between items-center">
                        <p className="font-medium text-gray-600">Total Assets</p>
                        <p className="font-semibold text-lg text-gray-800">{formatCurrency(totalAssets)}</p>
                    </div>
                )}
                {composition.length > 1 && (
                    <div className="rounded-xl bg-slate-50/70 p-3.5 -mt-1">
                        <button
                            type="button"
                            onClick={() => setShowComposition((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 text-left"
                            aria-expanded={showComposition}
                        >
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Where it sits</span>
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${showComposition ? 'rotate-180' : ''}`} />
                        </button>
                        <div className="mt-2 flex h-2.5 rounded-full overflow-hidden bg-gray-200">
                            {composition.map((slice) => (
                                <div
                                    key={slice.id}
                                    style={{ width: `${slice.pct}%`, backgroundColor: slice.color }}
                                    title={`${slice.label} ${formatCurrency(slice.amount)}`}
                                />
                            ))}
                        </div>
                        {showComposition && (
                            <div className="mt-3 flex flex-col gap-2">
                                {composition.map((slice) => (
                                    <div key={slice.id} className="flex items-center gap-2 text-xs">
                                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: slice.color }} />
                                        <span className="text-gray-600 flex-grow truncate">{slice.label}</span>
                                        {/* No percentage column: the bar above already says the
                                            proportion, and the three columns together left the
                                            label about 110px in the narrowest laid-out column —
                                            enough to truncate "Shares / managed funds" to a letter
                                            and a full stop. */}
                                        <span className="text-gray-800 font-semibold shrink-0 tabular-nums">{formatCurrency(slice.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className="flex justify-between items-center">
                    <p className="font-medium text-gray-600">Total Liabilities</p>
                    <p className="font-semibold text-lg text-rose-500">({formatCurrency(totalLiabilities)})</p>
                </div>
                <div className="flex justify-between items-start border-t-2 border-gray-800 pt-3 mt-3 gap-2">
                    <p className="font-bold text-gray-800 flex items-center gap-1">
                        Estimated net worth
                        {showStructureSplit && (
                            <InfoIcon text={structuresFootnote} />
                        )}
                    </p>
                    <GradientText className="font-extrabold text-2xl shrink-0">{formatCurrency(displayNetWorth)}</GradientText>
                </div>
                {showStructureSplit && (
                    <p className="text-xs text-gray-500 leading-snug">{structuresFootnote}</p>
                )}
                <div className="mt-6 pt-4 border-t border-dashed">
                    <div className="flex justify-between items-center cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                        <p className="font-medium text-gray-600 flex items-center gap-2"><DollarSign size={16} /> Est. Annual Cash Flow</p>
                        <p className={`font-semibold text-lg ${cashFlow >= 0 ? 'text-green-600' : 'text-rose-600'}`}>{formatCurrency(cashFlow)}</p>
                        <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                    {isExpanded && (
                        <div className="mt-2 pl-4 pr-2 py-2 bg-gray-50 rounded-lg">
                            {cashFlowBreakdown.map((item, idx) => {
                                let tooltipTitle = null;
                                let tooltipData = null;
                                if (item.label && item.label.includes('Age Pension')) {
                                    tooltipTitle = 'Age Pension Breakdown (estimate)';
                                    tooltipData = socialSecurityBreakdown && socialSecurityBreakdown.length > 0
                                        ? socialSecurityBreakdown
                                        : [{ label: 'No Age Pension payable or not eligible.', value: '' }];
                                } else if (item.label && item.label.includes('Tax')) {
                                    tooltipTitle = 'Tax Breakdown (estimate)';
                                    tooltipData = taxBreakdown && taxBreakdown.length > 0
                                        ? taxBreakdown
                                        : [{ label: 'No tax payable.', value: '' }];
                                }
                                // A `note` row explains the figures rather than being one of them, so
                                // it is styled apart: no green/red, and set off by a rule above. Money
                                // the client is taxed on but never receives must not read like income.
                                const isNote = (item as { note?: boolean }).note === true;
                                return (
                                    <div
                                        key={item.label ? item.label + idx : idx}
                                        className={`flex justify-between items-center text-xs py-1 rounded px-1 ${isNote ? 'mt-1 pt-2 border-t border-gray-200 italic' : 'hover:bg-gray-200/50'}`}
                                    >
                                        <div className="flex items-center gap-1">
                                            <p className="text-gray-500">{item.label}</p>
                                            {(tooltipTitle && tooltipData) && (
                                                <CalculationTooltip title={tooltipTitle} data={tooltipData}>
                                                    <Info size={14} className="text-blue-400 cursor-help" />
                                                </CalculationTooltip>
                                            )}
                                        </div>
                                        <p className={`font-medium ${isNote ? 'text-gray-400' : Number(item.value) >= 0 ? 'text-green-600' : 'text-rose-500'}`}>{formatCurrency(item.value)}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default FinancialSnapshot;
